import axios from "axios";
import apiClient from "./apiClient";
import { _post } from "./apiClient";
import { apiUrls } from "../config/apiUrls.const";
import { AccountPayload, ProfilePayload } from "../types";
import { getSecureItem, setSecureItem } from "../utils/LocalStorageManager";

export const loginUser = async (email: string, password: string) => {
  const response = await apiClient.post(apiUrls.login, { email, password });

  const loginData = response.data;
  const accessToken = loginData?.data?.token?.access_token;
  const refreshToken = loginData?.data?.token?.refresh_token;

  if (accessToken) {
    setSecureItem("access_token", accessToken);
    setSecureItem("refresh_token", refreshToken);

    const userDataResponse = await apiClient.get(apiUrls.user);
    const user = userDataResponse?.data?.data;

    if (user) {
      setSecureItem("user", user);

      if (user?.roles?.length > 0) {
        const hasSparlonRole = user?.roles?.some((role: any) => role.identifier === 'sparlon');
        if (hasSparlonRole) {
          try {
            const businesses = await apiClient.get(apiUrls.getBusinesses);
            setSecureItem("businesses", businesses?.data?.data);
          } catch (e) {
            console.error("Failed to fetch businesses", e);
          }
        }
      }

      return user; // Return the user object for the UI to use
    }
  }

  throw new Error(loginData.message || "Login failed");
};

export const registerUser = async (
  name: string,
  mobile_no: string,
  email: string,
  password: string,
  password_confirmation: string,
  type: string,
  allowed_service: string
) => {
  try {
    const payload: Record<string, any> = {
      name,
      mobile_no,
      email,
      password,
      password_confirmation,
      type,
      allowed_service
    };

    const { data } = await apiClient.post(apiUrls.register, payload);

    if (data.data?.token?.access_token) {
      const accessToken = data.data.token.access_token;
      const refreshToken = data.data.token.refresh_token;
      if (accessToken) {
        setSecureItem("access_token", accessToken);
        setSecureItem("refresh_token", refreshToken);

        const userDataResponse = await apiClient.get(apiUrls.user);
        const user = userDataResponse?.data?.data;

        if (user) {
          setSecureItem("user", user);

          return { success: true, user: user };
        }
      }
    }

    throw new Error("Registration failed");
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;

      if (status === 422 && data.errors) {
        return {
          success: false,
          type: "validation",
          errors: data.errors,
        };
      }

      if (status === 400 || status === 401) {
        return {
          success: false,
          type: "auth",
          message: data.message || "Invalid registration attempt.",
        };
      }
    }

    return {
      success: false,
      type: "server",
      message: "Something went wrong. Please try again later.",
    };
  }
};

export const resetUserData = async () => {
  const userData = await apiClient.get(apiUrls.user);
  const user = userData?.data?.data;
  setSecureItem("user", user);
  if (user?.roles?.length > 0) {
    const hasSparlonRole = user?.roles?.some((role: any) => role.identifier === 'sparlon');
    if (hasSparlonRole) {
      try {
        const businesses = await apiClient.get(apiUrls.getBusinesses);
        setSecureItem("businesses", businesses?.data?.data);
      } catch (e) {
        console.error("Failed to fetch businesses", e);
      }
    }
  }
  return userData;
};

export const getUser = async () => {
  const { data } = await apiClient.get(apiUrls.user);
  return data;
};

export const getProfile = async () => {
  const { data } = await apiClient.get(apiUrls.profile);
  return data;
};

export const updateProfile = (payload: Partial<ProfilePayload>) => {
  return _post(apiUrls.updateProfile, payload);
};

export const updateAccount = (payload: Partial<AccountPayload>) => {
  return _post(apiUrls.updateAccount, payload);
};

export const updatePhoto = async (formData: FormData) => {
  const { data } = await apiClient.post(apiUrls.updatePhoto, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return data;
};

export const getDecodedToken = () => {
  try {
    const token = getSecureItem("access_token");
    if (!token) return null;

    const payload = JSON.parse(atob(token.split(".")[1])); // decode JWT payload
    return payload;
  } catch (error) {
    console.error("Invalid token", error);
    return null;
  }
};

export const logout = async () => {
  try {
    await apiClient.post(apiUrls.logout); 
  } catch (error) {
    console.warn("Logout request failed, but clearing local data anyway:", error);
  } finally {
    // 💡 Use your explicit LocalStorageManager removal tools if available, 
    // or clear them like this to leave configurations like 'location' untouched:
    localStorage.removeItem("secure_access_token"); // Adjust if your getSecureItem applies prefixes
    localStorage.removeItem("secure_refresh_token");
    localStorage.removeItem("secure_user");
    localStorage.removeItem("secure_businesses");

    // Clear un-prefixed fallbacks
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    localStorage.removeItem("businesses");

    window.location.href = "/";
  }
};